<script setup>
  import { ref, computed, nextTick, watchEffect, useTemplateRef } from 'vue'
  import { useI18n } from 'vue-i18n';
  import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';  
  import { useSheetDrag } from '@/composables/useSheetDrag';
  import CloseButton from '../CloseButton.vue';

  defineOptions({
    inheritAttrs: false
  })  

  const emits = defineEmits(['close']);

  // TODO: Add click out side to close
  const props = defineProps({
    allowOutsideClick: {
      type: Boolean,
      default: true
    },
    hideCloseButton: {
      type: Boolean,
      default: false
    },
    // 'icon': the X close button; 'done': a "Done" button in its place
    closeButton: {
      type: String,
      default: 'icon'
    },
    width: {
      type: String,
      default: 'auto'
    },
    height: {
      type: String,
      default: 'auto'
    },
    styleClasses: {
      type: String,
      required: false
    },
    unstyled: {
      type: Boolean,
      default: false
    },
    // 'dialog': centered box (zooms in on desktop, slides up on phones)
    // 'sheet': near-full-height panel anchored to the bottom that slides up at every width
    variant: {
      type: String,
      default: 'dialog'
    },
    cleanup: {
      type: Function,
      default: () => {}
    }
  });

  // Sheet resizing (useSheetDrag), after the map sheets (SheetResizer, sheet.scss). Released,
  // the sheet snaps to the nearest of its minimized height, these viewport fractions (the
  // map sheets' small and mid heights) or its normal size, and closes below half its
  // minimized height, as the map sheets do. Flick speeds are useSheetDrag's defaults.
  const SHEET_MINIMIZED_HEIGHT = 100;
  const SHEET_CLOSE_HEIGHT = SHEET_MINIMIZED_HEIGHT / 2;
  const SHEET_SNAP_POINTS = [0.4, 0.6];
  // Must match the sheet's slide duration in modal.scss
  const SHEET_TRANSITION_DURATION = 300;

  const model = defineModel();

  const { t } = useI18n();

  const grabber = useTemplateRef('grabber');

  let modal = ref(null);
  let showModal = ref(false);

  // Space above the sheet in px while resized by the user; null = the CSS default
  const sheetTopGap = ref(null);
  // The sheet's top gap at its normal size, measured before the first resize
  let defaultSheetTopGap = null;

  const sheetStyles = computed(() => sheetTopGap.value === null
    ? null
    : { '--jwx-modal-sheet-top-gap': `${sheetTopGap.value}px` });

  // The backdrop's dimming (null = the CSS default, full). The first drag below the sheet's
  // normal size fades it with the drag, and it fades out completely when released there.
  // It never fades back in while dragging up; it fades in once the sheet is back at its
  // normal size.
  const backdropOpacity = ref(null);
  // True while the dimming follows the pointer (its CSS transition is off then)
  const isBackdropTracking = ref(false);

  const backdropStyles = computed(() => backdropOpacity.value === null
    ? null
    : { '--jwx-modal-backdrop-opacity': backdropOpacity.value });

  const transitionDuration = computed(() => props.variant === 'sheet' ? SHEET_TRANSITION_DURATION : undefined);

  const { activate, deactivate } = useFocusTrap(modal, {
    allowOutsideClick: handleClickOutside,
    escapeDeactivates: handleEscape,
    initialFocus: getInitialFocus,
    returnFocusOnDeactivate: false
  })

  const { isDragging: isDraggingSheet } = useSheetDrag(grabber, {
    getHeight: getCurrentSheetHeight,
    getSnapHeights: getSheetSnapHeights,
    closeHeight: SHEET_CLOSE_HEIGHT,
    onStart: handleSheetDragStart,
    onDrag: handleSheetDrag,
    onSettle: handleSheetSettle,
    onClose: handleCloseModal
  });

  // A sheet's first focusable element is its grabber; start on the close button instead
  // (undefined = focus-trap's default, the first focusable element)
  function getInitialFocus() {
    if (props.variant !== 'sheet') return undefined;

    return modal.value?.querySelector('.jwx-modal__close-button, .jwx-modal__done-button') ?? undefined;
  }

  async function activateFocusTrap() {
    await nextTick();
    activate();
  }

  function handleCloseModal() {
    model.value = false;
    deactivate();

    // emits('close');
  }

  function handleClickOutside() {
    if (!props.allowOutsideClick) return;
    handleCloseModal();
  }

  // Escape closes the modal like the close button does. Returning false keeps focus-trap
  // from ending the trap on its own (it used to, leaving the modal open with no trap);
  // closing deactivates it instead. Modals without a close button ignore Escape.
  function handleEscape() {
    if (!props.hideCloseButton) handleCloseModal();

    return false;
  }

  function getCurrentSheetHeight() {
    return window.innerHeight - modal.value.getBoundingClientRect().top;
  }

  function getSheetHeights() {
    return {
      height: window.innerHeight - sheetTopGap.value,
      defaultHeight: window.innerHeight - defaultSheetTopGap
    };
  }

  // Snap heights in px, smallest first
  function getSheetSnapHeights() {
    const { defaultHeight } = getSheetHeights();
    const snapHeights = SHEET_SNAP_POINTS.map(point => window.innerHeight * point);

    return [SHEET_MINIMIZED_HEIGHT, ...snapHeights.filter(height => height < defaultHeight), defaultHeight];
  }

  // While dragging: back at the normal size the dimming fades in; below it, it follows the
  // drag down from where it is but never back up, and stays out once it's out
  function updateBackdropWhileDragging() {
    const { height, defaultHeight } = getSheetHeights();
    const currentOpacity = backdropOpacity.value ?? 1;

    if (height >= defaultHeight) {
      isBackdropTracking.value = false;
      backdropOpacity.value = 1;
      return;
    }

    if (currentOpacity === 0) return;

    const dragOpacity = Math.max((height - SHEET_CLOSE_HEIGHT) / (defaultHeight - SHEET_CLOSE_HEIGHT), 0);

    isBackdropTracking.value = true;
    backdropOpacity.value = Math.min(currentOpacity, dragOpacity);
  }

  // Once the sheet settles the dimming is all or nothing: out below the normal size, in at it
  function settleBackdrop() {
    const { height, defaultHeight } = getSheetHeights();

    isBackdropTracking.value = false;
    backdropOpacity.value = height < defaultHeight ? 0 : 1;
  }

  // Measure the normal size before the first resize changes it
  function handleSheetDragStart() {
    if (defaultSheetTopGap === null) defaultSheetTopGap = window.innerHeight - getCurrentSheetHeight();
  }

  function handleSheetDrag(height) {
    sheetTopGap.value = window.innerHeight - height;
    updateBackdropWhileDragging();
  }

  function handleSheetSettle(height) {
    sheetTopGap.value = window.innerHeight - height;
    settleBackdrop();
  }

  watchEffect(() => {    
    showModal.value = model.value;

    if (!showModal.value) return;

    activateFocusTrap();
  })

  watchEffect(() => {
    if (showModal.value) return;

    deactivate();
    // A resized sheet reopens at its default size
    sheetTopGap.value = null;
    defaultSheetTopGap = null;
    backdropOpacity.value = null;
    isBackdropTracking.value = false;
    props.cleanup();
  })
</script>

<template>
  <Teleport to="body">
    <!-- Sheets fade only the backdrop's ::before layer, so Vue can't read the duration from
         the backdrop itself; it's given explicitly -->
    <Transition name="show-modal" :duration="transitionDuration" @after-leave="emits('close')">
      <div
        v-if="showModal"
        class="jwx-modal-backdrop"
        :data-variant="props.variant"
        :data-dim-tracking="isBackdropTracking || null"
        :style="backdropStyles"
      >
        <div
          ref="modal"
          class="jwx-modal"
          :class="props.styleClasses"
          :data-width="props.width"
          :data-height="props.height"
          :data-unstyled="props.unstyled"
          :data-variant="props.variant"
          :data-close-button="props.hideCloseButton ? null : props.closeButton"
          :data-dragging="isDraggingSheet || null"
          :style="sheetStyles"
          v-bind="$attrs"
        >
          <div
            v-if="props.variant === 'sheet'"
            ref="grabber"
            class="jwx-modal__grabber"
            role="separator"
            aria-orientation="horizontal"
            :aria-label="t('common.resize')"
            tabindex="0"
          ></div>
          <div class="jwx-modal__content">
            <template v-if="!props.hideCloseButton">
              <button
                v-if="props.closeButton === 'done'"
                @click.prevent="handleCloseModal"
                type="button"
                class="jwx-button-control jwx-modal__done-button"
                data-surface="1"
                data-shape="pill"
              >
                {{ t('common.done') }}
              </button>
              <CloseButton v-else @click.prevent="handleCloseModal" class="jwx-modal__close-button" />
            </template>
  
            <slot></slot>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>